import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

/**
 * Export Trial Balance to Excel
 * @param {Object} params
 * @param {Array} params.trialBalData - Array of trial balance rows
 * @param {string} params.fromDate - Start date (YYYY-MM-DD)
 * @param {string} params.toDate - End date (YYYY-MM-DD)
 * @param {string} params.societyName - Society/Company name
 */
export const exportTrialBalExcel = ({ trialBalData, fromDate, toDate, societyName }) => {
  const wb = XLSX.utils.book_new();
  const wsData = [];

  /** --- Format helpers --- */
  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    const numberFormat = new Intl.NumberFormat("en-IN", {
      style: "decimal",
      minimumFractionDigits: 2,
    });
    return numberFormat.format(amount || 0);
  };

  const reportTitle = `Trial Balance : ${formatDate(fromDate)} to ${formatDate(toDate)}`;

  /** --- Title & Header Section --- */
  wsData.push([societyName]);
  wsData.push([reportTitle]);
  wsData.push([]); // spacer

  /** --- Two-tier Header --- */
  wsData.push([
    "Particular",
    "Opening Balance",
    "",
    "Transactions",
    "",
    "Closing Balance",
    "",
  ]);
  wsData.push([
    "",
    "Amount (Dr)",
    "Amount (Cr)",
    "Amount (Dr)",
    "Amount (Cr)",
    "Amount (Dr)",
    "Amount (Cr)",
  ]);

  /** --- Data Rows --- */
  if (trialBalData?.length) {
    trialBalData.forEach((tb) => {
      const isTotal = tb.particular === "GRAND TOTAL";

      wsData.push([
        tb.particular || "",
        formatCurrency(tb.open_bal_dr),
        formatCurrency(tb.open_bal_cr),
        formatCurrency(tb.tran_dr),
        formatCurrency(tb.tran_cr),
        formatCurrency(tb.closing_dr),
        formatCurrency(tb.closing_cr),
      ]);

      // Optional empty row after total
      if (isTotal) wsData.push([]);
    });
  } else {
    wsData.push(["No record found."]);
  }

  /** --- Create Worksheet --- */
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  /** --- Merges (title + two-tier headers) --- */
  ws["!merges"] = [
    // Title row merges
    { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },

    // Header tier 1 merges
    { s: { r: 3, c: 1 }, e: { r: 3, c: 2 } }, // Opening
    { s: { r: 3, c: 3 }, e: { r: 3, c: 4 } }, // Transactions
    { s: { r: 3, c: 5 }, e: { r: 3, c: 6 } }, // Closing
  ];

  /** --- Styling --- */
  const range = XLSX.utils.decode_range(ws["!ref"]);
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[cellRef];
      if (!cell) continue;

      // Title styling
      if (R === 0) {
        cell.s = {
          font: { bold: true, sz: 14 },
          alignment: { horizontal: "center" },
        };
      } else if (R === 1) {
        cell.s = {
          font: { italic: true, sz: 12 },
          alignment: { horizontal: "center" },
        };
      }

      // Header tier 1 (labels)
      if (R === 3) {
        cell.s = {
          font: { bold: true },
          alignment: { horizontal: "center", vertical: "center" },
          fill: { fgColor: { rgb: "8CB3DA" } },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };
      }

      // Header tier 2 (subheaders)
      if (R === 4) {
        cell.s = {
          font: { bold: true },
          alignment: { horizontal: "center" },
          fill: { fgColor: { rgb: "DCE6F1" } },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } },
          },
        };
      }

      // Body rows
      if (R > 4) {
        const isNumericCol = [1, 2, 3, 4, 5, 6].includes(C);
        const rowVal = wsData[R]?.[0]?.toString()?.toUpperCase?.();

        // Grand total row styling
        if (rowVal === "GRAND TOTAL") {
          cell.s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "F0F0F0" } },
            alignment: { horizontal: isNumericCol ? "right" : "left" },
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
            },
          };
        } else {
          cell.s = {
            alignment: { horizontal: isNumericCol ? "right" : "left" },
          };
        }
      }
    }
  }

  /** --- Column widths --- */
  ws["!cols"] = [
    { wch: 40 }, // Particular
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
  ];

  /** --- Add to workbook and save --- */
  XLSX.utils.book_append_sheet(wb, ws, "Trial Balance");

  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([wbout], { type: "application/octet-stream" }),
    `Trial_Balance_${formatDate(fromDate)}_to_${formatDate(toDate)}.xlsx`
  );
};
